<script setup lang="ts">
import { Check, Search } from "lucide-vue-next";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { apiClient, ApiError } from "@/api/client";
import type { SubjectSummary } from "@/api/types";
import MonogramTile from "@/components/ui/MonogramTile.vue";
import RlPill from "@/components/ui/RlPill.vue";
import RlSegmented from "@/components/ui/RlSegmented.vue";
import RlSkeleton from "@/components/ui/RlSkeleton.vue";
import SlideOverPanel from "@/components/ui/SlideOverPanel.vue";
import { recoveryKeyFor, recoveryNotice } from "@/lib/entitlement";
import { useCatalogueStore } from "@/stores/catalogue";
import { useHomeStore } from "@/stores/home";

/**
 * "Add a subject" — the former Catalogue page folded into a slide-over
 * (PRD PE-5.1..PE-5.4). A subject list is a detour from whatever you were
 * doing, never a destination, so it has no route of its own.
 *
 * Self-managing: this component owns the `?add=1` query param end to end, so
 * hosts mount it once with no props and any "Add subject" affordance just
 * pushes the param. Clearing the param with `replace` (not `push`) keeps the
 * panel out of history — a back-navigation after adding a subject should
 * return to the previous page, not reopen the panel.
 */

const emit = defineEmits<{ enrolled: [subjectId: string] }>();

const route = useRoute();
const router = useRouter();
const catalogue = useCatalogueStore();
const home = useHomeStore();

const open = computed(() => route.query.add === "1");

const subjects = ref<SubjectSummary[]>([]);
const loading = ref(false);
const loadError = ref<string | null>(null);
const actionError = ref<string | null>(null);
/**
 * The route out of `actionError`, when it has one. §10's paywall refusals
 * each point somewhere different — a plan to start, a payer to chase, a
 * mailbox to confirm — and an error that names a state without naming the
 * step out of it is the dead end §10 warns about.
 */
const actionErrorLink = ref<{ to: string; label: string } | null>(null);

const levelFilter = ref("all");
const query = ref("");
const pendingIds = ref<Set<string>>(new Set());
/**
 * Subjects enrolled during this panel session. Held separately from the home
 * store so the row flips to "Added" on click rather than one network
 * round-trip later; the store refresh that follows is what the sidebar reads.
 */
const optimisticIds = ref<Set<string>>(new Set());

// `level` is written by the ingest pipeline as exactly "GCSE" or "A-level"
// (scripts/pipeline.py:_level_for_board), so these values match on equality.
const LEVEL_OPTIONS = [
  { value: "all", label: "All" },
  { value: "GCSE", label: "GCSE" },
  { value: "A-level", label: "A-Level" },
] as const;

const enrolledIds = computed(() => {
  const ids = new Set(optimisticIds.value);
  for (const subject of home.subjects) {
    ids.add(subject.subject_id);
  }
  return ids;
});

const visibleSubjects = computed(() => {
  const needle = query.value.trim().toLowerCase();
  return subjects.value.filter((subject) => {
    if (levelFilter.value !== "all" && subject.level !== levelFilter.value)
      return false;
    if (!needle) return true;
    return (
      subject.subject.toLowerCase().includes(needle) ||
      subject.exam_body.toLowerCase().includes(needle) ||
      subject.title.toLowerCase().includes(needle)
    );
  });
});

async function loadSubjects(): Promise<void> {
  loading.value = true;
  loadError.value = null;
  try {
    // The whole published catalogue is a few hundred rows, so it is fetched
    // once and filtered in memory. Round-tripping per keystroke would make
    // search feel worse than the page this replaces.
    const [all] = await Promise.all([apiClient.listSubjects(), home.load()]);
    subjects.value = all;
  } catch (e) {
    loadError.value =
      e instanceof ApiError && e.status === 401
        ? "Sign in again to browse subjects."
        : "Could not load subjects. Try again in a moment.";
  } finally {
    loading.value = false;
  }
}

watch(
  open,
  (isOpen) => {
    if (!isOpen) return;
    actionError.value = null;
    actionErrorLink.value = null;
    optimisticIds.value = new Set();
    query.value = "";
    levelFilter.value = "all";
    void loadSubjects();
  },
  { immediate: true },
);

function close(): void {
  const { add: _add, ...rest } = route.query;
  void router.replace({ path: route.path, query: rest, hash: route.hash });
}

/** PE-5.4: every failure path says something true; none of them fail silently. */
function applyEnrolError(e: unknown): void {
  if (!(e instanceof ApiError)) {
    actionError.value = "Could not add that subject. Try again in a moment.";
    return;
  }
  // Keyed on the CODE, never on the status. The paywall spans a 402 and a
  // 403, so keying it on status alone would render
  // `pending_email_verification` as a closed door and tell a learner whose
  // seat is already paid for that they have no access (§11.1.10); and the
  // two purchase-blocked 403s mean the account and the subject are both
  // fine, so neither may share copy with a genuine refusal.
  //
  // The words come from `lib/entitlement.ts` rather than from a map here
  // (rl-757.48). This panel and §4.5's notice render several of the same
  // codes, and two tables is how one refusal ends up with two voices
  // depending on which surface the reader reached first.
  const key = recoveryKeyFor(e);
  if (key !== null) {
    const view = recoveryNotice({
      key,
      // This surface loads no subscription, so it cannot know who funds the
      // seat or whether the reader may buy one. Both unknowns are passed as
      // such: the funder-less voice never tells a holder somebody else's
      // payment is theirs to fix, and `canSelfPurchase: null` is the
      // permissive tri-state — the age answer arrives here as its own code
      // (`age_below_purchase_threshold`) rather than as a flag.
      funderFirstName: null,
      funderEmail: null,
      canSelfPurchase: null,
      readerOwesPayment: false,
      hadPlan: false,
      endsAt: null,
    });
    actionError.value = view.body;
    // The refusal's route out is a `RouterLink`, so the mail hand-off — the
    // one external control this family offers — is not renderable here and
    // is skipped rather than pushed into a `to`. It needs a funder this
    // surface does not load anyway.
    const route = view.controls.find((control) => !control.external);
    actionErrorLink.value =
      route === undefined ? null : { to: route.target, label: route.label };
    return;
  }
  switch (e.status) {
    case 403:
      // A 403 with no row is a closed door, or a code from a server newer
      // than this build. Codes are additive and never renamed, so an
      // unrecognised one must not be guessed into the paywall.
      actionError.value = "You do not have access to that subject.";
      break;
    case 404:
      actionError.value = "That subject is no longer available.";
      break;
    default:
      actionError.value = "Could not add that subject. Try again in a moment.";
  }
}

async function add(subject: SubjectSummary): Promise<void> {
  if (pendingIds.value.has(subject.id) || enrolledIds.value.has(subject.id))
    return;
  actionError.value = null;
  actionErrorLink.value = null;
  pendingIds.value = new Set(pendingIds.value).add(subject.id);
  optimisticIds.value = new Set(optimisticIds.value).add(subject.id);
  try {
    await apiClient.enrollSubject(subject.id);
    // The sidebar reads the home store, so refreshing it is what makes the
    // new subject appear without a reload. `enrolledSubjectIds` is mirrored
    // for the views that still read the catalogue store.
    catalogue.enrolledSubjectIds = new Set(catalogue.enrolledSubjectIds).add(
      subject.id,
    );
    emit("enrolled", subject.id);
    await home.refresh();
  } catch (e) {
    const reverted = new Set(optimisticIds.value);
    reverted.delete(subject.id);
    optimisticIds.value = reverted;
    if (e instanceof ApiError && e.status === 401) {
      await router.push("/login");
      return;
    }
    applyEnrolError(e);
  } finally {
    const next = new Set(pendingIds.value);
    next.delete(subject.id);
    pendingIds.value = next;
  }
}
</script>

<template>
  <SlideOverPanel
    v-if="open"
    title="Add a subject"
    subtitle="Everything's included in your plan."
    @close="close"
  >
    <div class="add-filters">
      <RlSegmented
        v-model="levelFilter"
        :options="LEVEL_OPTIONS"
        label="Filter subjects by level"
        class="add-levels"
      />
      <div class="add-search">
        <Search class="add-search-icon" :size="15" aria-hidden="true" />
        <input
          v-model="query"
          type="search"
          class="add-search-input"
          placeholder="Search subjects or boards…"
          aria-label="Search subjects or boards"
          data-testid="add-subject-search"
        />
      </div>
    </div>

    <p
      v-if="actionError"
      class="add-action-error"
      role="alert"
      data-testid="add-subject-error"
    >
      {{ actionError }}
      <RouterLink
        v-if="actionErrorLink"
        :to="actionErrorLink.to"
        @click="close"
      >
        {{ actionErrorLink.label }}
      </RouterLink>
    </p>

    <div v-if="loading" class="add-loading">
      <div v-for="n in 6" :key="n" class="add-row">
        <RlSkeleton width="36px" :height="36" />
        <div class="add-row-main">
          <RlSkeleton width="55%" :height="13" />
          <RlSkeleton width="35%" :height="11" />
        </div>
      </div>
    </div>

    <p v-else-if="loadError" class="add-empty" role="alert">{{ loadError }}</p>

    <ul v-else-if="visibleSubjects.length" class="add-list">
      <li v-for="subject in visibleSubjects" :key="subject.id" class="add-row">
        <MonogramTile :name="subject.subject" :size="36" />
        <div class="add-row-main">
          <p class="add-row-name">{{ subject.subject }}</p>
          <div class="add-row-pills">
            <RlPill>{{ subject.exam_body }}</RlPill>
            <RlPill>{{ subject.level }}</RlPill>
          </div>
        </div>
        <span
          v-if="enrolledIds.has(subject.id)"
          class="add-added"
          data-testid="subject-added"
        >
          <Check :size="14" aria-hidden="true" />
          Added
        </span>
        <button
          v-else
          type="button"
          class="secondary add-button"
          :disabled="pendingIds.has(subject.id)"
          data-testid="add-subject-button"
          @click="add(subject)"
        >
          {{ pendingIds.has(subject.id) ? "Adding…" : "Add" }}
        </button>
      </li>
    </ul>

    <p v-else class="add-empty">No subjects match your search.</p>
  </SlideOverPanel>
</template>

<style scoped>
.add-filters {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  padding-bottom: var(--sp-4);
  border-bottom: 1px solid var(--rl-border-subtle);
}

.add-levels {
  display: flex;
}

.add-levels :deep(.segment) {
  flex: 1;
}

.add-search {
  position: relative;
}

.add-search-icon {
  position: absolute;
  top: 50%;
  left: var(--sp-3);
  color: var(--rl-fg-3);
  transform: translateY(-50%);
  pointer-events: none;
}

.add-search-input {
  width: 100%;
  height: 40px;
  padding: 0 var(--sp-3) 0 var(--sp-8);
  font-family: var(--rl-font-sans);
  font-size: var(--rl-text-md);
  color: var(--rl-fg-1);
  background: var(--rl-bg);
  border: 1px solid var(--rl-border);
  border-radius: var(--radius);
}

.add-action-error {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2);
  margin: var(--sp-4) 0 0;
  padding: var(--sp-3);
  font-size: var(--rl-text-sm);
  color: var(--rl-danger-fg);
  background: var(--rl-danger-bg);
  border: 1px solid var(--rl-danger-bd);
  border-radius: var(--radius);
}

.add-action-error a {
  font-weight: 600;
  color: inherit;
}

.add-list,
.add-loading {
  margin: 0;
  padding: 0;
  list-style: none;
}

.add-row {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-3) 0;
  border-bottom: 1px solid var(--rl-border-subtle);
}

.add-row-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--sp-1);
  min-width: 0;
}

.add-row-name {
  margin: 0;
  font-size: var(--rl-text-md);
  font-weight: 600;
  color: var(--rl-fg-1);
}

.add-row-pills {
  display: flex;
  gap: var(--sp-1);
}

.add-added {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: var(--sp-1);
  font-size: var(--rl-text-sm);
  font-weight: 600;
  color: var(--rl-mint-600);
}

.add-button {
  flex: none;
  padding: var(--sp-1) var(--sp-4);
  font-size: var(--rl-text-sm);
}

.add-empty {
  padding: var(--sp-10) 0;
  font-size: var(--rl-text-md);
  color: var(--rl-fg-3);
  text-align: center;
}
</style>
